import { useMemo, useRef, useState, type FormEvent } from 'react';
import type { DeviceCategory } from '@mvcp/contracts';
import type {
  DeviceCatalogEntry,
  DeviceDuplicateMatch,
  VendorDirectoryEntry,
} from '@mvcp/contracts';
import { checkDeviceDuplicate, proposeDevice } from '../api';
import { COUNTRY_NAME_OPTIONS } from '../lib/country';
import { DEVICE_CATEGORY_OPTIONS } from '../lib/device-category';
import { MAX_TEXT_LEN } from '../lib/field-limits';
import { deviceLabel, deviceRevision } from './device-label';

const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

/**
 * Name ONE existing match for the advisory warning (Story 8.5, AC3).
 *
 * The label itself comes from the web's single owner (`device-label.ts`, AD-25) — the
 * whole point of the warning is that the Manager sees the existing row's REVISION, so it
 * must read exactly as it reads in every picker. A match that records no revision says so
 * in words: "that already exists" without a revision is precisely the message a Manager
 * cannot act on, because the version is the only thing that makes two such rows distinct.
 * A pending proposal is marked, because "already in the catalog" and "you already
 * proposed this" are different situations with different next steps.
 */
function describeMatch(match: DeviceDuplicateMatch): string {
  const named = deviceLabel(match);
  // `deviceRevision`, not raw truthiness: a whitespace-only stored `version` is truthy while
  // the label owner (rightly) drops it, so the raw test named neither a revision nor its
  // absence — it rendered the bare model and asserted nothing, which is the one outcome this
  // sentence exists to prevent. The owner decides what counts as a revision; this decides
  // what to say about it.
  const withRevision = deviceRevision(match) ? named : `${named} (no revision recorded)`;
  return match.proposed ? `${withRevision} — your pending proposal` : withRevision;
}

/**
 * Propose a new Device when its model is absent from the catalog (Story 2.5, FR
 * propose-new). Mounted in TWO places: the `SiteDetail` install picker (2.4/2.5,
 * where a Manager discovers the gap mid-install) and the `DeviceDirectory` toolbar
 * (Story 7.1, the Devices page's own propose affordance). Either way it creates a
 * **global** proposed Device, never a site-scoped one — install stays the separate
 * 2.4 action, so proposing here never puts the Device in service. The Vendor
 * `<select>` is populated from `vendors` (the caller's Directory = curated + own
 * proposals; propose the Vendor first if it is also absent). Mirrors the
 * `SitesView` create-form pattern: in-flight guard, inline `role="alert"` error,
 * submit-label swap. Category and Version (Story 7.1 — the hardware/firmware
 * revision) are optional and omitted from the request when blank. On success calls
 * `onCreated(entry)` so the parent reloads (no auto-install).
 *
 * **Story 8.5 AC3 — the duplicate disclosure is ADVISORY, never a refusal.** When the
 * caller's visible catalog already holds this vendor + model the Manager is TOLD, before
 * the row is written, and is shown each existing revision — and may still propose. A
 * second revision of one vendor + model is a legitimate catalog state a Compliance
 * Manager must be able to create (`K-Chief 700 v7.2` alongside `v8.0`); it is exactly the
 * state AD-25's labels and the merge guard exist to disambiguate. So the submit control
 * stays **enabled**, `onSubmit` is untouched, there is no acknowledgement and no second
 * step, and nothing gates on an in-flight or failed check.
 *
 * Three properties of the trigger are deliberate:
 *   - It fires on **every identity edit** — the model input's and the vendor
 *     `<select>`'s `onChange`, whenever both are non-empty — so the verdict is already on
 *     screen by the time any submit is possible. This is the one mechanism that satisfies
 *     told-before-written **and** no-second-step **and** always-creatable at once. A
 *     `blur`-only trigger (the first pass) delivered the disclosure on NEITHER ordinary
 *     submit path: pressing Enter in the model input submits without firing `blur` at all,
 *     so the row was created with the Manager never told; and clicking Propose blurs and
 *     starts the check *fire-and-forget* while `onSubmit` proceeds, so the check raced the
 *     create and the warning resolved after the row existed — the "report afterwards" the
 *     story forbids. Cost is constrained by MEMOIZING on `vendorId::lower(trim(model))`
 *     rather than by narrowing the trigger, so an unchanged pair is never re-queried; it
 *     is deliberately undebounced, because a debounce would put a timer in every test of
 *     this behaviour (the per-keystroke request volume is logged in `deferred-work.md`).
 *   - `onSubmit` still runs **no** check of its own. Checking there and refusing the first
 *     press would be the second confirm step this story forbids on propose — that shape
 *     belongs to the merge alone — so there is no gating, no acknowledgement and no
 *     in-flight dependency anywhere on the submit path.
 *   - The verdict comes from the SERVER (`GET /devices/duplicate-check`), inside the
 *     Manager's own tenant context, so it sees exactly the visible catalog and every
 *     caller reaching this check BY `vendorId` — including Story 8.9's inline form once a
 *     vendor is selected — inherits one owner of device identity. (Story 8.9 amended this
 *     clause: while the INLINE vendor path is active there is no `vendorId` to ask about,
 *     so no check is issued at all. See the inline section below; AD-25 clause 6 carries
 *     the same correction.) A client-side check is not an option: this form's two mounts
 *     hold different data (`DeviceDirectory` has `vendors` alone, `SiteDetail` the full
 *     catalog), so a client rule would be a second copy threaded from two sources.
 *   - **Absence of a warning is not a promise of uniqueness.** Another tenant's proposal
 *     is invisible (AR-6) and a failed check renders nothing at all (logged in
 *     `deferred-work.md`) — which is another reason the create must stay available.
 *
 * The advisory renders in the same region as the error but as its own `role="status"`
 * `aria-live="polite"` element with `data-tone="warn"`: an error is the result of an
 * action and must interrupt, while an advisory appears while the Manager is still filling
 * the form and an assertive region would talk over them mid-field. That polite region is
 * precisely *why* a during-typing trigger is safe. The warning can never go stale: every
 * identity edit re-asks, so what is on screen always describes the pair in the fields now.
 *
 * **Story 8.9 — the manufacturer can be minted here too.** A Manager whose device's Vendor
 * is also absent used to have to abandon this form, walk to the Vendor Directory, propose
 * there and come back; on a brand-new Company the propose affordance was disabled outright
 * beneath copy telling them to do exactly that. A **"The vendor isn't listed"** toggle now
 * reveals the same five fields `ProposeVendorForm` collects, and one request creates both
 * proposals in one server-side transaction (`DeviceRepository.propose`).
 *
 * Three properties of that path are deliberate:
 *   - **The `<select>` stays the DEFAULT and the inline fields are the escape hatch.**
 *     Inline creation makes minting a duplicate Vendor materially easier and this story is
 *     barred from building the dedup that would catch it (Story 8.10/8.12), so the cheapest
 *     available mitigation is that the Manager reads the directory before concluding it
 *     lacks their vendor. It is the same reasoning by which the DEVICE disclosure above is
 *     advisory rather than a gate: tell, then permit.
 *   - **No duplicate check is issued or implied while the inline path is active.** A Vendor
 *     being minted has no devices, so there is nothing visible for the model to match —
 *     an empty warning area here would be a promise of uniqueness the server never made.
 *     `onIdentityEdit` is therefore not called at all on this path, and switching to it
 *     bumps `checkSeq` so an answer still in flight for the previously selected vendor
 *     cannot land afterwards.
 *   - **The picker remains reachable**: choosing a vendor from the `<select>` leaves the
 *     inline path, restores `vendorId` as the identity, and re-asks the advisory for that
 *     pair. Switching back and forth cannot strand the form in a state with two identities.
 */
export function ProposeDeviceForm({
  vendors,
  onCreated,
  onCancel,
}: {
  vendors: VendorDirectoryEntry[];
  /**
   * The created catalog entry, plus whether this propose also MINTED the vendor
   * (Story 8.9). The parent cannot infer that from the entry: a Manager may equally
   * have picked an existing own-Company proposal from the directory, and that vendor
   * carries `proposedByCompanyId` too. Both parents need the fact — one to name the
   * vendor in its acknowledgement, both to re-fetch the vendor list whose picker would
   * otherwise still be missing the row just created.
   */
  /**
   * The parent's post-propose work. AWAITABLE as of Story 8.9's review round 2, and the
   * form DOES await it: both parents re-read two or three lists here and only then collapse
   * the form, so if this returned immediately `submitting` would go false while the form was
   * still mounted with its fields intact — and a second click would re-send the identical
   * body, minting a second Device and, on the inline path, a second Vendor. Round 2 proved
   * that empirically. Returning a promise is what makes "the form is busy until the parent
   * has finished" true rather than merely intended.
   */
  onCreated: (entry: DeviceCatalogEntry, mintedVendor: boolean) => void | Promise<void>;
  onCancel: () => void;
}): React.JSX.Element {
  const [vendorId, setVendorId] = useState('');
  const [model, setModel] = useState('');
  const [category, setCategory] = useState<DeviceCategory | ''>('');
  const [version, setVersion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Story 8.9: whether the manufacturer is being MINTED rather than picked. This is the
   * one derivation of "which identity form is in force" — the submit body, the advisory
   * trigger and the validation all read it, so they cannot disagree about which vendor
   * the Manager meant.
   */
  const [inlineVendor, setInlineVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [newVendorCountry, setNewVendorCountry] = useState('');
  const [newVendorCategory, setNewVendorCategory] = useState('');
  const [newVendorPrimaryProduct, setNewVendorPrimaryProduct] = useState('');
  const [newVendorComplianceContact, setNewVendorComplianceContact] = useState('');
  /** Story 8.5 AC3: the visible-catalog matches to disclose; `null` renders nothing. */
  const [duplicates, setDuplicates] = useState<DeviceDuplicateMatch[] | null>(null);
  /**
   * Sequence guard so a slow answer for an earlier vendor/model can never overwrite a
   * newer one — the warning must describe what is in the fields NOW or nothing at all.
   */
  const checkSeq = useRef(0);
  /**
   * Answers already known, keyed by `vendorId::lower(trim(model))` — the same identity the
   * server compares (`import.repository.ts:165`'s dedup key). This is how the cost of a
   * per-edit trigger is constrained without narrowing the trigger: an unchanged pair is
   * never re-queried, so tabbing out of the field, back in and out again asks once, and
   * re-typing a model the Manager already saw a verdict for answers from memory. A failure
   * is deliberately NOT cached — a transient outage must not silence the disclosure for
   * the rest of the session.
   */
  const answers = useRef(new Map<string, DeviceDuplicateMatch[]>());
  /** The selected vendor's name, so the warning names WHO already has the model. */
  const vendorName = vendors.find((e) => e.vendor.id === vendorId)?.vendor.name ?? '';

  /**
   * Story 8.9: `<datalist>` autocomplete for the inline Vendor's one remaining free-text
   * field, derived from the directory this form is already given — the same derivation
   * `VendorDirectory` does for `ProposeVendorForm`, so the two propose surfaces suggest the
   * same values. Advisory only: a Manager may still type a brand-new primary product, which
   * is the whole point of proposing. Both mounts pass `vendors`, so this is unconditional
   * rather than a capability one mount has and the other lacks.
   *
   * `countries` and `categories` were removed in DEV-4426 round 2 (QA TC-12) along with the
   * inputs they fed. Deriving suggestions from stored values is what made those two fields
   * self-widening — a typed value became the next Manager's offered option — so the
   * derivation goes with them rather than lingering unused.
   */
  const suggestions = useMemo(() => {
    const distinct = (vals: (string | undefined)[]): string[] =>
      [...new Set(vals.filter((v): v is string => !!v && v.trim() !== ''))].sort((a, b) =>
        a.localeCompare(b),
      );
    return {
      primaryProducts: distinct(vendors.map((e) => e.vendor.primaryProduct)),
    };
  }, [vendors]);

  /**
   * Enter or leave the inline-vendor path (Story 8.9).
   *
   * Entering drops `vendorId` — the two identities may never both be set, because the
   * server refuses a body carrying both — and RETIRES any duplicate verdict: the bump of
   * `checkSeq` is what stops an answer already in flight for the previously selected
   * vendor from arriving afterwards and rendering a warning about a pair that is no longer
   * in the fields.
   */
  function toggleInlineVendor(next: boolean): void {
    setInlineVendor(next);
    setError(null);
    if (next) setVendorId('');
    checkSeq.current += 1;
    setDuplicates(null);
  }

  /**
   * React to an edit of either identity field (Story 8.5, AC3) — the ONE trigger.
   *
   * Advisory only: it never blocks, never sets `error`, and a rejection renders nothing —
   * a quiet form means "nothing visible matched", not "this model is unique". Called from
   * `onChange`, never from `onSubmit`, so the verdict is on screen before a submit is
   * possible and the submit path stays free of any gate.
   */
  function onIdentityEdit(nextVendorId: string, nextModel: string): void {
    const trimmedModel = nextModel.trim();
    // Bump first: every edit invalidates any answer still in flight for an older pair, so
    // the warning can never describe a pair other than what is in the fields NOW.
    const seq = (checkSeq.current += 1);
    if (!nextVendorId || !trimmedModel) {
      setDuplicates(null);
      return;
    }
    const key = `${nextVendorId}::${trimmedModel.toLowerCase()}`;
    const known = answers.current.get(key);
    if (known) {
      setDuplicates(known.length > 0 ? known : null);
      return;
    }
    setDuplicates(null);
    void (async () => {
      try {
        const res = await checkDeviceDuplicate(nextVendorId, trimmedModel);
        answers.current.set(key, res.matches);
        if (seq !== checkSeq.current) return;
        setDuplicates(res.matches.length > 0 ? res.matches : null);
      } catch {
        // A transient failure shows NO warning rather than a scary one: the disclosure is
        // a courtesy, and turning it into an error would block a legitimate proposal.
        if (seq !== checkSeq.current) return;
        setDuplicates(null);
      }
    })();
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmedVendorName = newVendorName.trim();
    if (inlineVendor) {
      if (!trimmedVendorName) {
        setError('New vendor name is required.');
        return;
      }
    } else if (!vendorId) {
      setError('Select a vendor for this device.');
      return;
    }
    const trimmedModel = model.trim();
    if (!trimmedModel) {
      setError('Device model is required.');
      return;
    }
    setSubmitting(true);
    try {
      const entry = await proposeDevice({
        // Story 8.9: EXACTLY ONE identity form travels. `inlineVendor` is the single
        // derivation of which, so the body can never carry both (a 400) or neither.
        ...(inlineVendor
          ? {
              vendor: {
                name: trimmedVendorName,
                // Blank ⇒ the key is OMITTED, exactly as `ProposeVendorForm` sends it.
                ...(newVendorCountry.trim() ? { country: newVendorCountry.trim() } : {}),
                ...(newVendorCategory.trim() ? { category: newVendorCategory.trim() } : {}),
                ...(newVendorPrimaryProduct.trim()
                  ? { primaryProduct: newVendorPrimaryProduct.trim() }
                  : {}),
                ...(newVendorComplianceContact.trim()
                  ? { complianceContact: newVendorComplianceContact.trim() }
                  : {}),
              },
            }
          : { vendorId }),
        model: trimmedModel,
        ...(category ? { category } : {}),
        // Story 7.1: blank ⇒ the key is OMITTED, never sent as '' or null (the
        // server would 400/store an empty string and the tables would show it).
        ...(version.trim() ? { version: version.trim() } : {}),
      });
      // AWAITED, so this form stays disabled until the parent's re-reads land and it
      // collapses the form (review round 2 — see the `onCreated` prop docblock). The propose
      // itself has already SUCCEEDED at this point, so a failure in the parent's reload must
      // not be reported here as a failed propose: the parent owns surfacing it, and telling
      // the Manager "Couldn't propose" about a row that exists would be the worse lie.
      try {
        await onCreated(entry, inlineVendor);
      } catch {
        /* the parent reports its own reload failures */
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Couldn't propose. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      noValidate
      onSubmit={onSubmit}
      className="stack"
      aria-label="Propose a new device"
      style={{ gap: 10 }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' }}>
        <label className="stack" style={{ gap: 4, flex: '1 1 220px' }}>
          <span className="field-label">Model</span>
          <input
            aria-label="Device model"
            value={model}
            onChange={(e) => {
              setModel(e.target.value);
              // Story 8.5 AC3: EVERY identity edit re-asks. `blur` cannot be the trigger —
              // Enter in this input submits without ever firing one, and clicking Propose
              // fires it too late to be told before the row is written.
              //
              // Story 8.9: …unless the vendor is being MINTED, in which case there is no
              // vendorId to ask about and a vendor with no devices can match nothing.
              if (!inlineVendor) onIdentityEdit(vendorId, e.target.value);
            }}
            maxLength={MAX_TEXT_LEN}
            style={fieldStyle}
          />
        </label>
        <label className="stack" style={{ gap: 4, flex: '1 1 200px' }}>
          <span className="field-label">Vendor</span>
          <select
            aria-label="Vendor"
            value={vendorId}
            onChange={(e) => {
              setVendorId(e.target.value);
              setError(null);
              // Story 8.9: picking from the directory LEAVES the inline path — the
              // identity is a `vendorId` again — so the two can never both be set.
              if (e.target.value) setInlineVendor(false);
              // Story 8.5 AC3: the other half of the identity — drop the previous vendor's
              // verdict and ask for this one.
              //
              // Not while the inline path stays in force (review round 1): selecting the
              // empty placeholder leaves `inlineVendor` true, and firing the trigger there
              // contradicted this form's own docblock. It issued no request — the check
              // returns early on an empty vendorId — but a guard that holds only by
              // accident downstream is not the guarantee the docblock claims.
              if (e.target.value || !inlineVendor) onIdentityEdit(e.target.value, model);
            }}
            style={fieldStyle}
          >
            <option value="">Select a vendor…</option>
            {vendors.map((e) => (
              <option key={e.vendor.id} value={e.vendor.id}>
                {e.vendor.name}
                {e.vendor.category ? ` — ${e.vendor.category}` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="stack" style={{ gap: 4, flex: '0 1 170px' }}>
          <span className="field-label">Category</span>
          {/* A closed list as of 039, so a select rather than a text input: the API now
              rejects anything outside DEVICE_CATEGORIES, and offering a free-text box for a
              value the server will refuse invites a 400 the proposer cannot predict. The
              empty option is "not recorded", which stays distinct from `Other`. */}
          <select
            aria-label="Device category"
            value={category}
            onChange={(e) => setCategory(e.target.value as DeviceCategory | '')}
            style={fieldStyle}
          >
            <option value="">Select…</option>
            {DEVICE_CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="stack" style={{ gap: 4, flex: '0 1 150px' }}>
          <span className="field-label">Version</span>
          <input
            aria-label="Device version"
            placeholder="optional"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            maxLength={MAX_TEXT_LEN}
            style={fieldStyle}
          />
        </label>
      </div>

      {/* Story 8.9 — the escape hatch when the manufacturer is absent too. Deliberately
          NOT the default: the Manager should read the directory first (see the docblock),
          and the picker above stays live so choosing a vendor comes straight back here.
          `aria-controls` names the region this reveals (review round 1) — `aria-expanded`
          alone tells an AT user that something opened without saying what.
          Set ONLY while the region exists (review round 2): the region is conditionally
          rendered, so an unconditional `aria-controls` was an unresolvable IDREF whenever it
          was collapsed — invalid per axe `aria-valid-attr-value`, and some AT drop the
          relationship entirely, which is the opposite of the attribute's purpose. */}
      <div>
        <button
          type="button"
          className="icon-button"
          onClick={() => toggleInlineVendor(!inlineVendor)}
          aria-expanded={inlineVendor}
          {...(inlineVendor ? { 'aria-controls': 'propose-device-inline-vendor' } : {})}
          disabled={submitting}
        >
          {inlineVendor ? 'Choose an existing vendor instead' : 'The vendor isn’t listed'}
        </button>
      </div>

      {inlineVendor && (
        <div className="stack" id="propose-device-inline-vendor" style={{ gap: 8 }}>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            This vendor will be proposed together with the device — both are created in one
            step and both wait for admin verification.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' }}>
            <label className="stack" style={{ gap: 4, flex: '1 1 220px' }}>
              <span className="field-label">New vendor name</span>
              <input
                aria-label="New vendor name"
                value={newVendorName}
                onChange={(e) => {
                  setNewVendorName(e.target.value);
                  setError(null);
                }}
                maxLength={MAX_TEXT_LEN}
                style={fieldStyle}
              />
            </label>
            <label className="stack" style={{ gap: 4, flex: '0 1 170px' }}>
              <span className="field-label">Country</span>
              <select
                aria-label="New vendor country"
                value={newVendorCountry}
                onChange={(e) => setNewVendorCountry(e.target.value)}
                style={fieldStyle}
              >
                <option value="">—</option>
                {COUNTRY_NAME_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            {/* The VENDOR's category, not the Device's — the Device has its own selector
                above, closed since 039. QA's report separates the two explicitly because
                only this one was free text. */}
            <label className="stack" style={{ gap: 4, flex: '0 1 170px' }}>
              <span className="field-label">Vendor category</span>
              <select
                aria-label="New vendor category"
                value={newVendorCategory}
                onChange={(e) => setNewVendorCategory(e.target.value)}
                style={fieldStyle}
              >
                <option value="">—</option>
                {DEVICE_CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="stack" style={{ gap: 4, flex: '1 1 200px' }}>
              <span className="field-label">Primary product</span>
              <input
                aria-label="New vendor primary product"
                list="propose-device-vendor-products"
                value={newVendorPrimaryProduct}
                onChange={(e) => setNewVendorPrimaryProduct(e.target.value)}
                maxLength={MAX_TEXT_LEN}
                style={fieldStyle}
              />
            </label>
            <label className="stack" style={{ gap: 4, flex: '1 1 200px' }}>
              <span className="field-label">Compliance contact</span>
              <input
                aria-label="New vendor compliance contact"
                value={newVendorComplianceContact}
                onChange={(e) => setNewVendorComplianceContact(e.target.value)}
                maxLength={MAX_TEXT_LEN}
                style={fieldStyle}
              />
            </label>
          </div>
          {/* Distinct ids from `ProposeVendorForm`'s: the two forms never co-mount today,
              but duplicate DOM ids would silently point one form's autocomplete at the
              other's options the day they do. */}
          <datalist id="propose-device-vendor-products">
            {suggestions.primaryProducts.map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
        </div>
      )}

      {/* The submit and cancel controls come AFTER the inline vendor region (review round
          1). They used to sit in the field row above the disclosure, so tabbing forward
          from the Device model reached "Propose device" BEFORE "New vendor name" — the one
          field whose absence makes this submit fail — and a keyboard user on the inline
          path met the button before the requirement. Submit is last in the form because it
          is last in the task. */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="submit" className="btn-accent" data-surface="accent" disabled={submitting}>
          {submitting ? 'Proposing…' : 'Propose device'}
        </button>
        <button type="button" className="icon-button" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
      </div>

      <div className="stack" style={{ gap: 6, minHeight: 16 }}>
        <div role="alert" aria-live="assertive">
          {error && (
            <span className="status-pill" data-tone="bad">
              <span className="status-pill__dot" aria-hidden="true" />
              {error}
            </span>
          )}
        </div>
        {/*
          Story 8.5 AC3 — the advisory duplicate disclosure. Same region as the error, its
          own live element: `role="status"` + `aria-live="polite"` because it appears while
          the Manager is still in the form (an assertive region would interrupt them
          mid-field), and `data-tone="warn"` because it is not an error state. It never
          disables the submit control — the Manager is told, compares revisions, decides.
        */}
        <div role="status" aria-live="polite">
          {duplicates && (
            <span className="status-pill" data-tone="warn">
              <span className="status-pill__dot" aria-hidden="true" />
              {vendorName || 'This vendor'} already has this model in your catalog:{' '}
              {duplicates.map(describeMatch).join('; ')}. You can still propose it — do so
              only if you are recording a different revision.
            </span>
          )}
        </div>
      </div>
    </form>
  );
}
